/**
 * apps/web :: pages :: CheckoutSuccessPage
 *
 * `/checkout/success?session_id=...` — the page Stripe redirects users
 * to after a successful Checkout (FE-02, ROADS-445).
 *
 * Stripe substitutes `{CHECKOUT_SESSION_ID}` in our success URL with
 * the real `cs_*` id at redirect time; we don't actually need the id
 * to grant access (BACK-02 webhook does that), but we display the
 * trailing fragment so support can correlate against Stripe's
 * dashboard if anything goes sideways.
 *
 * Polling pattern:
 *   - On mount we hit `GET /api/entitlements/me` once; if any active
 *     entitlement exists we go straight to the celebrate state.
 *   - Otherwise we poll every 2s up to a 30s budget. Webhooks usually
 *     land in < 5s but Stripe doesn't promise an upper bound and we
 *     don't want to wedge the user on a spinner forever.
 *   - At budget exhaustion we show "give it a minute and refresh"
 *     copy with a manual refresh button. The webhook may still be
 *     in-flight; this page never charges or grants access — it just
 *     reflects the truth that BACK-02 has written.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, AlertTriangle, Sparkles } from "lucide-react";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import {
  listMyEntitlements,
  type Entitlement,
} from "@/lib/entitlement-api";

type Stage =
  | { kind: "polling" }
  | { kind: "granted"; entitlements: readonly Entitlement[] }
  | { kind: "timeout" };

/** Echo of the FE-04 credit fields (CheckoutPage stashed before Stripe redirect). */
interface UpgradeCreditEcho {
  session_id: string;
  credit_applied_cents: number;
  credit_method: string;
}

const UPGRADE_CREDIT_KEY = "rb_checkout_upgrade_credit";

function readUpgradeCreditEcho(
  currentSessionId: string | null,
): UpgradeCreditEcho | null {
  try {
    const raw = window.sessionStorage.getItem(UPGRADE_CREDIT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UpgradeCreditEcho;
    // Only honor the echo when it matches the session_id Stripe just
    // redirected with — otherwise it's stale state from a previous
    // checkout the user navigated away from.
    if (currentSessionId && parsed.session_id !== currentSessionId) return null;
    return parsed;
  } catch {
    return null;
  }
}

const POLL_INTERVAL_MS = 2_000;
const POLL_BUDGET_MS = 30_000;

/**
 * Where the "continue" CTA should land after a successful purchase.
 *
 * Route to the surface for the most-recently-granted active entitlement —
 * i.e. the thing the user just bought. Pass tiers (`tourist_out` /
 * `tourist_in`) live on the `/pass` surface, which the learner portal does
 * NOT link to; without this a pass buyer lands on `/dashboard` and can never
 * find their purchase. Everything else (class_c / cdl / motorcycle) belongs
 * on the learner dashboard. (ROADS-576 follow-up.)
 */
function destinationForPurchase(
  entitlements: readonly Entitlement[],
): "/pass" | "/dashboard" {
  const newest = [...entitlements].sort(
    (a, b) => b.created_at_ms - a.created_at_ms,
  )[0];
  return newest && (newest.tier === "tourist_out" || newest.tier === "tourist_in")
    ? "/pass"
    : "/dashboard";
}

export default function CheckoutSuccessPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const sessionId = search.get("session_id") ?? null;

  const [stage, setStage] = useState<Stage>({ kind: "polling" });
  const startedAtRef = useRef<number>(Date.now());
  // FE-04: read the credit echo once on mount; the value is set by
  // CheckoutPage right before the Stripe redirect. Clear it after read
  // so a navigate-away-and-back doesn't show the credit twice.
  const creditEcho = useMemo<UpgradeCreditEcho | null>(() => {
    const echo = readUpgradeCreditEcho(sessionId);
    if (echo) {
      try {
        window.sessionStorage.removeItem(UPGRADE_CREDIT_KEY);
      } catch {
        // best effort; the per-tab nature limits leakage anyway.
      }
    }
    return echo;
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const resp = await listMyEntitlements();
        if (cancelled) return;
        const active = resp.entitlements.filter(
          (e) => e.expires_at_ms > resp.now_ms,
        );
        if (active.length > 0) {
          setStage({ kind: "granted", entitlements: active });
          return;
        }
      } catch {
        // Network blip; we'll re-try on the next tick.
      }
      if (cancelled) return;
      if (Date.now() - startedAtRef.current >= POLL_BUDGET_MS) {
        setStage({ kind: "timeout" });
        return;
      }
      setTimeout(() => {
        if (!cancelled) void poll();
      }, POLL_INTERVAL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleContinue(): void {
    const dest =
      stage.kind === "granted"
        ? destinationForPurchase(stage.entitlements)
        : "/dashboard";
    navigate(dest);
  }

  function handleRefresh(): void {
    startedAtRef.current = Date.now();
    setStage({ kind: "polling" });
  }

  return (
    <LearnerPortalShell pageTitle={t("checkout.success.title")}>
      <section
        data-testid="checkout-success-page"
        className="mx-auto max-w-2xl px-md py-lg"
      >
        {stage.kind === "polling" && (
          <div data-testid="checkout-success-polling" role="status" aria-live="polite">
            <div className="flex items-center gap-sm text-slate-700">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                {t("checkout.success.confirmingTitle")}
              </h1>
            </div>
            <p className="mt-md text-base text-slate-600">
              {t("checkout.success.confirmingBody")}
            </p>
            {sessionId && (
              <p className="mt-md text-xs text-slate-500">
                <code data-testid="checkout-success-session-id">
                  {t("checkout.success.sessionRef", { id: sessionId })}
                </code>
              </p>
            )}
          </div>
        )}

        {stage.kind === "granted" && (
          <div data-testid="checkout-success-granted">
            <div className="flex items-center gap-sm text-emerald-700">
              <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
              <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                {t("checkout.success.grantedTitle")}
              </h1>
            </div>
            <p className="mt-md text-base text-slate-700">
              {t("checkout.success.grantedBody")}
            </p>
            {creditEcho && creditEcho.credit_applied_cents > 0 ? (
              <p
                data-testid="checkout-success-credit-applied"
                className="mt-md flex items-center gap-sm rounded-xl border border-amber-200 bg-amber-50 px-md py-sm text-sm text-amber-900"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                <span>
                  {t("checkout.success.creditApplied", {
                    credit: (creditEcho.credit_applied_cents / 100).toFixed(2),
                  })}
                </span>
              </p>
            ) : null}
            <button
              type="button"
              onClick={handleContinue}
              data-testid="checkout-success-continue"
              className="mt-lg inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
            >
              {t(
                destinationForPurchase(stage.entitlements) === "/pass"
                  ? "checkout.success.continueToPass"
                  : "checkout.success.continue",
              )}
            </button>
          </div>
        )}

        {stage.kind === "timeout" && (
          <div data-testid="checkout-success-timeout" role="alert">
            <div className="flex items-center gap-sm text-amber-700">
              <AlertTriangle className="h-6 w-6" aria-hidden="true" />
              <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                {t("checkout.success.timeoutTitle")}
              </h1>
            </div>
            <p className="mt-md text-base text-slate-700">
              {t("checkout.success.timeoutBody")}
            </p>
            <div className="mt-lg flex flex-wrap gap-sm">
              <button
                type="button"
                onClick={handleRefresh}
                data-testid="checkout-success-refresh"
                className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              >
                {t("checkout.success.refresh")}
              </button>
              <button
                type="button"
                onClick={handleContinue}
                className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-md text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              >
                {t("checkout.success.continueAnyway")}
              </button>
            </div>
          </div>
        )}
      </section>
    </LearnerPortalShell>
  );
}
